import React, { useState, useEffect } from 'react';
import { X, User, Mail, Shield, Save, Building2, Check } from 'lucide-react';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface Client {
  uid: string;
  name: string;
  display_name?: string;
  active?: boolean;
}

interface UserData {
  uid: string;
  email: string;
  name: string;
  lastname: string;
  user_type: 'user' | 'admin' | 'superadmin';
  created_at: any;
  client?: string;
  clients?: string[];
  assigned_clients?: string[];
}

interface EditUserModalProps {
  user: UserData;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: user.name || '',
    lastname: user.lastname || '',
    user_type: user.user_type || 'user',
    assigned_clients: user.assigned_clients || user.clients || (user.client ? [user.client] : [])
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {

      fetchClients();
      // Update form data when user changes
      const assignedClients = user.assigned_clients || user.clients || (user.client ? [user.client] : []);

      setFormData({
        name: user.name || '',
        lastname: user.lastname || '',
        user_type: user.user_type || 'user',
        assigned_clients: assignedClients
      });
    }
  }, [isOpen, user]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const clientsData: Client[] = [];

      clientsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.active !== false) {
          clientsData.push({
            uid: doc.id,
            name: data.name,
            display_name: data.display_name || data.name,
            active: data.active
          });
        }
      });

      setClients(clientsData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const toggleClient = (clientId: string) => {
    setFormData(prev => {
      const newAssignedClients = prev.assigned_clients.includes(clientId)
        ? prev.assigned_clients.filter(id => id !== clientId)
        : [...prev.assigned_clients, clientId];

      return {
        ...prev,
        assigned_clients: newAssignedClients
      };
    });
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Update user in Firestore
      const userRef = doc(db, 'users', user.uid);
      const updateData = {
        ...formData,
        updated_at: new Date()
      };

      await updateDoc(userRef, updateData);

      onSave();
      onClose();
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Error al actualizar el usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Editar Usuario
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Email (Read-only) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Email
            </label>
            <input
              type="email"
              value={user.email}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Nombre
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Lastname */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Apellido
            </label>
            <input
              type="text"
              value={formData.lastname}
              onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* User Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Shield className="w-4 h-4 inline mr-2" />
              Tipo de Usuario
            </label>
            <select
              value={formData.user_type}
              onChange={(e) => setFormData({ ...formData, user_type: e.target.value as 'user' | 'admin' | 'superadmin' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="user">Usuario</option>
              <option value="admin">Administrador</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>

          {/* Clients Multi-Select */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-2" />
              Clientes Asignados
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                disabled={loading || loadingClients}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-gray-700">
                  {loadingClients ? 'Cargando clientes...' :
                   formData.assigned_clients.length === 0 ? 'Seleccionar clientes...' :
                   `${formData.assigned_clients.length} cliente${formData.assigned_clients.length > 1 ? 's' : ''} seleccionado${formData.assigned_clients.length > 1 ? 's' : ''}`}
                </span>
                <X
                  className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {dropdownOpen && !loadingClients && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {clients.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No hay clientes disponibles
                    </div>
                  ) : (
                    clients.map((client) => (
                      <div
                        key={client.uid}
                        onClick={() => toggleClient(client.uid)}
                        className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                      >
                        <span className="text-sm text-gray-700">
                          {client.display_name || client.name}
                        </span>
                        {formData.assigned_clients.includes(client.uid) && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Selected clients tags */}
            {formData.assigned_clients.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.assigned_clients.map((clientId) => {
                  const client = clients.find(c => c.uid === clientId);
                  return (
                    <span
                      key={clientId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      {client?.display_name || client?.name || clientId}
                      <button
                        type="button"
                        onClick={() => toggleClient(clientId)}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;